import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import { GET_ALL_GISTS, GET_ALL_SUBJECTS } from "../../services/graphql/queriesMutations";
import styles from "./InfiniteGistSheetPage.module.css";

type LabSubject = {
  id: number;
  title: string;
  parentId: number | null;
};

type LabUser = {
  id?: number;
  name?: string | null;
  image?: string | null;
};

type LabComment = {
  id: number;
  comment?: string | null;
  parentId?: number | null;
  gistId?: number | null;
  editId?: number | null;
  user?: LabUser | null;
  createdAt?: string | null;
};

type LabEdit = {
  id: number;
  body?: string | null;
  createdAt?: string | null;
  comments?: LabComment[] | null;
};

type LabVersion = {
  id: number;
  point?: string | null;
  createdAt?: string | null;
  edits?: LabEdit[] | null;
  user?: LabUser | null;
};

type LabGist = {
  id: number;
  title: string;
  parentId?: number | null;
  subjectId?: number | null;
  status?: string | null;
  createdAt?: string | null;
  versions?: LabVersion[] | null;
};

type GistsQuery = {
  gists: LabGist[];
};

type SubjectsQuery = {
  subjects: LabSubject[];
};

type FlatSubject = LabSubject & {
  depth: number;
  rootGistCount: number;
  directGistCount: number;
};

type GistNode = {
  gist: LabGist;
  children: GistNode[];
  depth: number;
  totalDescendants: number;
};

type PositionedGistNode = GistNode & {
  x: number;
  y: number;
};

type PositionedLink = {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  hot: boolean;
};

type GistTreeResult = {
  roots: GistNode[];
  visibleCount: number;
  rootCount: number;
};

const CARD_WIDTH = 330;
const CARD_HEIGHT = 205;
const SUBJECT_X = 70;
const SUBJECT_Y = 130;
const FIRST_GIST_X = 560;
const COLUMN_GAP = 460;
const ROW_GAP = 255;
const TOP_ALIGNED_Y = 80;
const MIN_CANVAS_WIDTH = 1600;
const MIN_CANVAS_HEIGHT = 980;

function stripHtml(input?: string | null): string {
  if (!input) return "";
  if (typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    const doc = new window.DOMParser().parseFromString(input, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return input.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function excerpt(input?: string | null, limit = 210): string {
  const text = stripHtml(input);
  if (!text) return "No body text available in the latest edit.";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}…`;
}

function formatDate(date?: string | null): string {
  if (!date) return "unknown date";
  const parsed = new Date(date).getTime();
  if (Number.isNaN(parsed)) return "unknown date";
  return new Date(parsed).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getLatestVersion(gist: LabGist | null): LabVersion | null {
  if (!gist?.versions?.length) return null;
  return gist.versions
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
}

function getLatestEdit(gist: LabGist | null): LabEdit | null {
  const version = getLatestVersion(gist);
  if (!version?.edits?.length) return null;
  return version.edits
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
}

function getCommentCount(gist: LabGist): number {
  const seen: { [id: number]: true } = {};
  let count = 0;
  (gist.versions || []).forEach((version) => {
    (version.edits || []).forEach((edit) => {
      (edit.comments || []).forEach((comment) => {
        if (!seen[comment.id]) {
          seen[comment.id] = true;
          count += 1;
        }
      });
    });
  });
  return count;
}

function sortGistsChronologically(gists: LabGist[]): LabGist[] {
  return gists.slice().sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return aTime - bTime;
  });
}

function getChildSubjectIds(subjects: LabSubject[], subjectId: number): number[] {
  const result: number[] = [];
  const visit = (parentId: number) => {
    subjects
      .filter((subject) => subject.parentId === parentId)
      .forEach((child) => {
        result.push(child.id);
        visit(child.id);
      });
  };
  visit(subjectId);
  return result;
}

function buildGistIdMap(gists: LabGist[]): { [id: number]: LabGist } {
  const map: { [id: number]: LabGist } = {};
  gists.forEach((gist) => {
    map[gist.id] = gist;
  });
  return map;
}

function buildReplyBuckets(gists: LabGist[]): { [parentId: number]: LabGist[] } {
  const buckets: { [parentId: number]: LabGist[] } = {};
  gists.forEach((gist) => {
    if (typeof gist.parentId !== "number") return;
    buckets[gist.parentId] = buckets[gist.parentId] || [];
    buckets[gist.parentId].push(gist);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[Number(key)] = sortGistsChronologically(buckets[Number(key)]);
  });

  return buckets;
}

function getRootGistsForSubjects(gists: LabGist[], subjectIds: number[]): LabGist[] {
  const allowedSubjects: { [id: number]: true } = {};
  subjectIds.forEach((id) => {
    allowedSubjects[id] = true;
  });

  const candidates = gists.filter((gist) => typeof gist.subjectId === "number" && allowedSubjects[gist.subjectId]);
  const candidateIds: { [id: number]: true } = {};
  candidates.forEach((gist) => {
    candidateIds[gist.id] = true;
  });

  const roots = candidates.filter((gist) => {
    if (typeof gist.parentId !== "number") return true;
    return !candidateIds[gist.parentId];
  });

  return sortGistsChronologically(roots);
}

function buildFlatSubjects(subjects: LabSubject[], gists: LabGist[]): FlatSubject[] {
  const byParent: { [parentId: string]: LabSubject[] } = {};
  subjects.forEach((subject) => {
    const key = subject.parentId === null ? "root" : String(subject.parentId);
    byParent[key] = byParent[key] || [];
    byParent[key].push(subject);
  });

  Object.keys(byParent).forEach((key) => {
    byParent[key] = byParent[key].slice().sort((a, b) => a.title.localeCompare(b.title));
  });

  const directCounts: { [subjectId: number]: number } = {};
  const rootCounts: { [subjectId: number]: number } = {};
  const gistById = buildGistIdMap(gists);

  gists.forEach((gist) => {
    if (typeof gist.subjectId !== "number") return;
    directCounts[gist.subjectId] = (directCounts[gist.subjectId] || 0) + 1;
    const parent = typeof gist.parentId === "number" ? gistById[gist.parentId] : null;
    const parentInSameSubject = parent && parent.subjectId === gist.subjectId;
    if (!parentInSameSubject) {
      rootCounts[gist.subjectId] = (rootCounts[gist.subjectId] || 0) + 1;
    }
  });

  const flat: FlatSubject[] = [];
  const visit = (parentId: number | null, depth: number) => {
    const key = parentId === null ? "root" : String(parentId);
    (byParent[key] || []).forEach((subject) => {
      flat.push({
        ...subject,
        depth,
        rootGistCount: rootCounts[subject.id] || 0,
        directGistCount: directCounts[subject.id] || 0,
      });
      visit(subject.id, depth + 1);
    });
  };

  visit(null, 0);
  return flat;
}

function getSubjectTitle(subjects: LabSubject[], subjectId: number | null): string {
  if (!subjectId) return "No subject selected";
  return subjects.find((subject) => subject.id === subjectId)?.title || `Subject ${subjectId}`;
}

function getSubjectPath(subjects: LabSubject[], subjectId: number | null): LabSubject[] {
  if (!subjectId) return [];
  const byId: { [id: number]: LabSubject } = {};
  subjects.forEach((subject) => {
    byId[subject.id] = subject;
  });

  const path: LabSubject[] = [];
  let current: LabSubject | undefined = byId[subjectId];
  let guard = 0;
  while (current && guard < 50) {
    path.unshift(current);
    current = current.parentId === null ? undefined : byId[current.parentId];
    guard += 1;
  }
  return path;
}

function buildGistTree(gists: LabGist[], selectedSubjectIds: number[]): GistTreeResult {
  const replyBuckets = buildReplyBuckets(gists);
  const rootGists = getRootGistsForSubjects(gists, selectedSubjectIds);
  const visibleIds: { [id: number]: true } = {};

  const markVisible = (gist: LabGist) => {
    if (visibleIds[gist.id]) return;
    visibleIds[gist.id] = true;
    (replyBuckets[gist.id] || []).forEach((child) => markVisible(child));
  };

  rootGists.forEach((gist) => markVisible(gist));

  const buildNode = (gist: LabGist, depth: number): GistNode => {
    const children = (replyBuckets[gist.id] || [])
      .filter((child) => visibleIds[child.id])
      .map((child) => buildNode(child, depth + 1));

    const totalDescendants = children.reduce((sum, child) => sum + 1 + child.totalDescendants, 0);
    return { gist, children, depth, totalDescendants };
  };

  return {
    roots: rootGists.map((gist) => buildNode(gist, 0)),
    visibleCount: Object.keys(visibleIds).length,
    rootCount: rootGists.length,
  };
}

function layoutGistTree(roots: GistNode[]): { nodes: PositionedGistNode[]; links: PositionedLink[]; canvasWidth: number; canvasHeight: number } {
  const nodes: PositionedGistNode[] = [];
  const links: PositionedLink[] = [];
  const nextYByDepth: { [depth: number]: number } = {};

  const takeNextY = (depth: number): number => {
    const y = nextYByDepth[depth] ?? TOP_ALIGNED_Y;
    nextYByDepth[depth] = y + ROW_GAP;
    return y;
  };

  const layoutNode = (node: GistNode): PositionedGistNode => {
    const positioned: PositionedGistNode = {
      ...node,
      x: FIRST_GIST_X + node.depth * COLUMN_GAP,
      y: takeNextY(node.depth),
    };

    nodes.push(positioned);

    node.children.forEach((child) => {
      const childPosition = layoutNode(child);
      links.push({
        id: `${positioned.gist.id}-${child.gist.id}`,
        fromX: positioned.x + CARD_WIDTH,
        fromY: positioned.y + Math.round(CARD_HEIGHT * 0.46),
        toX: childPosition.x,
        toY: childPosition.y + Math.round(CARD_HEIGHT * 0.46),
        hot: positioned.depth === 0,
      });
    });

    return positioned;
  };

  roots.forEach((root) => {
    const rootPosition = layoutNode(root);
    links.push({
      id: `subject-${rootPosition.gist.id}`,
      fromX: SUBJECT_X + 350,
      fromY: SUBJECT_Y + 98,
      toX: rootPosition.x,
      toY: rootPosition.y + Math.round(CARD_HEIGHT * 0.46),
      hot: true,
    });
  });

  const maxX = nodes.length ? Math.max.apply(null, nodes.map((node) => node.x)) : FIRST_GIST_X;
  const maxY = nodes.length ? Math.max.apply(null, nodes.map((node) => node.y)) : 180;

  return {
    nodes,
    links,
    canvasWidth: Math.max(MIN_CANVAS_WIDTH, maxX + CARD_WIDTH + 260),
    canvasHeight: Math.max(MIN_CANVAS_HEIGHT, maxY + CARD_HEIGHT + 190),
  };
}

function getDepthLabel(depth: number): string {
  if (depth === 0) return "Root Gist under Subject";
  if (depth === 1) return "Reply Gist · level 1";
  return `Reply Gist · level ${depth}`;
}

function getGistToneClass(depth: number): string {
  if (depth === 0) return styles.rootGistCard;
  if (depth === 1) return styles.replyGistCard;
  if (depth === 2) return styles.deepReplyGistCard;
  return styles.deeperReplyGistCard;
}

const InfiniteGistSheetPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSubjectId = useMemo(() => {
    const raw = searchParams.get("subjectId");
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const { data: gistData, loading: gistsLoading, error: gistsError } = useQuery<GistsQuery>(GET_ALL_GISTS, {
    fetchPolicy: "cache-and-network",
  });
  const { data: subjectData, loading: subjectsLoading, error: subjectsError } = useQuery<SubjectsQuery>(GET_ALL_SUBJECTS, {
    fetchPolicy: "cache-and-network",
  });

  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [includeChildSubjects, setIncludeChildSubjects] = useState(false);
  const [openedGistId, setOpenedGistId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  const subjects = useMemo(() => subjectData?.subjects || [], [subjectData]);
  const gists = useMemo(() => gistData?.gists || [], [gistData]);
  const flatSubjects = useMemo(() => buildFlatSubjects(subjects, gists), [subjects, gists]);

  useEffect(() => {
    if (!flatSubjects.length) return;

    if (requestedSubjectId && flatSubjects.some((subject) => subject.id === requestedSubjectId)) {
      if (selectedSubjectId !== requestedSubjectId) {
        setSelectedSubjectId(requestedSubjectId);
      }
      return;
    }

    if (selectedSubjectId && flatSubjects.some((subject) => subject.id === selectedSubjectId)) return;

    const firstWithRootGists = flatSubjects.find((subject) => subject.rootGistCount > 0);
    setSelectedSubjectId((firstWithRootGists || flatSubjects[0]).id);
  }, [flatSubjects, requestedSubjectId, selectedSubjectId]);

  const selectedSubjectIds = useMemo(() => {
    if (!selectedSubjectId) return [];
    if (!includeChildSubjects) return [selectedSubjectId];
    return [selectedSubjectId].concat(getChildSubjectIds(subjects, selectedSubjectId));
  }, [includeChildSubjects, selectedSubjectId, subjects]);

  const tree = useMemo(() => buildGistTree(gists, selectedSubjectIds), [gists, selectedSubjectIds]);
  const layout = useMemo(() => layoutGistTree(tree.roots), [tree.roots]);

  useEffect(() => {
    setOpenedGistId(null);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, top: 0 });
    }
  }, [selectedSubjectId, includeChildSubjects]);

  const selectedSubjectTitle = getSubjectTitle(subjects, selectedSubjectId);
  const selectedPath = getSubjectPath(subjects, selectedSubjectId);
  const openedNode = openedGistId ? layout.nodes.find((node) => node.gist.id === openedGistId) || null : null;
  const openedGist = openedNode?.gist || null;
  const openedVersion = getLatestVersion(openedGist);
  const openedEdit = getLatestEdit(openedGist);
  const openedProductionLink = openedGist?.subjectId
    ? `/subject/${encodeURIComponent(getSubjectTitle(subjects, openedGist.subjectId))}/${openedGist.subjectId}?gistId=${openedGist.id}`
    : openedGist
      ? `/gist/${encodeURIComponent(openedGist.title)}/${openedGist.id}`
      : "/";

  const handleSubjectClick = (subjectId: number) => {
    setSelectedSubjectId(subjectId);
    setSearchParams({ subjectId: String(subjectId) });
  };

  const handleCanvasMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, a, .gist-sheet-lab-card")) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startLeft: scrollEl.scrollLeft,
      startTop: scrollEl.scrollTop,
    };
    setIsDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const state = dragStateRef.current;
      scrollEl.scrollLeft = state.startLeft - (event.clientX - state.startX);
      scrollEl.scrollTop = state.startTop - (event.clientY - state.startY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const resetSheet = () => {
    scrollRef.current?.scrollTo({ left: 0, top: 0, behavior: "smooth" });
  };

  const loading = gistsLoading || subjectsLoading;
  const error = gistsError || subjectsError;

return (
    <main className={styles.pageShell}>
      <section className={styles.labFrame}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Sandbox · removable page · real Gist hierarchy</p>
            <h1>Infinite Gist Sheet by Subject</h1>
            <p className={styles.intro}>
              Pick a Subject on the left. The sheet now shows the actual parent → reply Gist tree, not a flat mockup grid.
            </p>
          </div>
          <div className={styles.headerPills}>
            <span>{tree.rootCount} root Gists</span>
            <span>{tree.visibleCount} Gists in visible reply tree</span>
            <span>{includeChildSubjects ? "child subjects included" : "direct subject only"}</span>
          </div>
        </header>

        <section className={styles.bodyGrid}>
          <aside className={styles.subjectRail}>
            <div className={styles.railHeader}>
              <strong>Subjects</strong>
              <span>Small badge shows root/total Gists directly assigned to this Subject.</span>
            </div>

            {loading && <div className={styles.loadingBlock}>Loading subjects and Gists…</div>}
            {error && <div className={styles.errorBlock}>Could not load data: {error.message}</div>}

            <div className={styles.subjectList}>
              {flatSubjects.map((subject) => {
                const isSelected = subject.id === selectedSubjectId;
                return (
                  <button
                    key={subject.id}
                    type="button"
                    className={`${styles.subjectButton} ${isSelected ? styles.subjectButtonActive : ""}`}
                    style={{ paddingLeft: `${12 + subject.depth * 18}px` }}
                    onClick={() => handleSubjectClick(subject.id)}
                    title={`${subject.rootGistCount} root Gists / ${subject.directGistCount} total Gists assigned directly to this Subject`}
                  >
                    <span>{subject.title}</span>
                    <small>{subject.rootGistCount}/{subject.directGistCount}</small>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className={styles.sheetPanel}>
            <div className={styles.sheetToolbar}>
              <div className={styles.pathBlock}>
                <span className={styles.pathLabel}>Selected subject</span>
                <strong>{selectedSubjectTitle}</strong>
                <em>{selectedPath.map((subject) => subject.title).join(" / ") || "No path"}</em>
              </div>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${includeChildSubjects ? styles.actionButtonActive : ""}`}
                  onClick={() => setIncludeChildSubjects((value) => !value)}
                >
                  {includeChildSubjects ? "Child subjects: on" : "Child subjects: off"}
                </button>
                <button type="button" className={styles.actionButton} onClick={resetSheet}>
                  Reset sheet
                </button>
              </div>
            </div>

            <div
              ref={scrollRef}
              className={`${styles.sheetScroll} ${isDragging ? styles.sheetScrollDragging : ""}`}
              onMouseDown={handleCanvasMouseDown}
            >
              <div className={styles.sheetCanvas} style={{ width: layout.canvasWidth, height: layout.canvasHeight }}>
                <svg className={styles.links} width={layout.canvasWidth} height={layout.canvasHeight} viewBox={`0 0 ${layout.canvasWidth} ${layout.canvasHeight}`}>
                  {layout.links.map((link) => {
                    const midX = Math.round((link.fromX + link.toX) / 2);
                    return (
                      <path
                        key={link.id}
                        className={`${styles.linkPath} ${link.hot ? styles.linkPathHot : ""}`}
                        d={`M ${link.fromX} ${link.fromY} C ${midX} ${link.fromY}, ${midX} ${link.toY}, ${link.toX} ${link.toY}`}
                      />
                    );
                  })}
                </svg>

                <article className={`${styles.subjectNucleus} gist-sheet-lab-card`} style={{ left: SUBJECT_X, top: SUBJECT_Y }}>
                  <span>Subject nucleus</span>
                  <h2>{selectedSubjectTitle}</h2>
                  <p>
                    This card is the starting point. First-level cards are root Gists directly under this Subject; deeper cards are real Reply Gists.
                  </p>
                  <div className={styles.cardChips}>
                    <span>{tree.rootCount} root Gists</span>
                    <span>{tree.visibleCount} visible total</span>
                  </div>
                </article>

                {tree.visibleCount === 0 && !loading && (
                  <div className={styles.emptySheet}>
                    <h2>No root Gists found for this subject.</h2>
                    <p>Try another subject, or turn on child subjects if the content lives inside a nested Subject.</p>
                  </div>
                )}

                {layout.nodes.map((node) => {
                  const gist = node.gist;
                  const latestVersion = getLatestVersion(gist);
                  const latestEdit = getLatestEdit(gist);
                  const commentCount = getCommentCount(gist);
                  return (
                    <article
                      key={gist.id}
                      className={`${styles.gistCard} ${getGistToneClass(node.depth)} gist-sheet-lab-card`}
                      style={{ left: node.x, top: node.y }}
                      onClick={() => setOpenedGistId(gist.id)}
                    >
                      <div className={styles.cardKicker}>{getDepthLabel(node.depth)} · #{gist.id} · {formatDate(gist.createdAt)}</div>
                      <h3>{gist.title}</h3>
                      <p>{excerpt(latestVersion?.point || latestEdit?.body, 190)}</p>
                      <div className={styles.cardChips}>
                        <span>{node.children.length} direct replies</span>
                        <span>{node.totalDescendants} total replies</span>
                        <span>{commentCount} comments</span>
                        <span>{gist.status || "status unknown"}</span>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        </section>
      </section>

      {openedGist && openedNode && (
        <section className={styles.overlay} onClick={() => setOpenedGistId(null)}>
          <article className={styles.openedGist} onClick={(event) => event.stopPropagation()}>
            <button type="button" className={styles.closeButton} onClick={() => setOpenedGistId(null)}>
              Close
            </button>
            <p className={styles.eyebrow}>Read-only Gist preview · {getDepthLabel(openedNode.depth)}</p>
            <h2>{openedGist.title}</h2>
            <p className={styles.openedPoint}>{stripHtml(openedVersion?.point) || "No summary available."}</p>
            <div className={styles.openedBody}>{stripHtml(openedEdit?.body) || "No body available in the latest edit."}</div>
            <div className={styles.openedFooter}>
              <span>{openedNode.children.length} direct Reply Gists</span>
              <span>{openedNode.totalDescendants} total descendant Reply Gists</span>
              <span>{getCommentCount(openedGist)} comments</span>
              <span>Subject: {getSubjectTitle(subjects, openedGist.subjectId || null)}</span>
              <Link to={openedProductionLink} className={styles.primaryLink}>
                Open production Gist route
              </Link>
              <Link to={`/comment-lab?gistId=${openedGist.id}`} className={styles.primaryLink}>
                Open Comment Lab
              </Link>
            </div>
          </article>
        </section>
      )}
    </main>
  );
};

export default InfiniteGistSheetPage;
